import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Pencil, Plus, Trash2, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { fmtMXN } from "../lib/api";

// Editor de líneas de una OC para Compras (pedido de Gustavo, 27/08).
// En pending_approval: agregar/quitar/subir/bajar cantidad (línea nueva toma
// SIEMPRE el precio de catálogo vigente). En approved: sólo bajar cantidad o
// quitar líneas — nunca agregar ni subir (el gasto aprobado es un techo).
export default function POEditItemsModal({ po, onClose, onSuccess }) {
  const isApproved = po.status === "approved";
  const [items, setItems] = useState(
    po.items.map((it) => ({ supply_id: it.supply_id, supply_name: it.supply_name, unit: it.unit, quantity: it.quantity, unit_price: it.unit_price }))
  );
  const [catalog, setCatalog] = useState([]);
  const [reason, setReason] = useState("");
  const [step, setStep] = useState("edit"); // edit | confirm
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isApproved) return;
    api.get(`/suppliers/${po.supplier_id}/supplies`, { params: { active_only: true, include_supply_details: true } })
      .then((r) => setCatalog(r.data))
      .catch(() => setCatalog([]));
  }, [isApproved, po.supplier_id]);

  const updateQty = (idx, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], quantity: Math.max(0, parseFloat(val) || 0) };
    setItems(next);
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const addItem = () => {
    const available = catalog.find((s) => !items.some((it) => it.supply_id === s.supply_id));
    if (!available) return;
    setItems([...items, {
      supply_id: available.supply_id, supply_name: available.supply.name, unit: available.supply.unit,
      quantity: 1, unit_price: available.unit_price,
    }]);
  };

  const setItemSupply = (idx, supply_id) => {
    const rel = catalog.find((s) => s.supply_id === supply_id);
    if (!rel) return;
    const next = [...items];
    next[idx] = { ...next[idx], supply_id, supply_name: rel.supply.name, unit: rel.supply.unit, unit_price: rel.unit_price };
    setItems(next);
  };

  const diff = () => {
    const oldById = {};
    po.items.forEach((it) => { oldById[it.supply_id] = it; });
    const newIds = new Set(items.map((i) => i.supply_id));
    const changes = [];
    po.items.forEach((it) => {
      if (!newIds.has(it.supply_id)) changes.push({ supply_name: it.supply_name, removed: true, before_qty: it.quantity });
    });
    items.forEach((it) => {
      const old = oldById[it.supply_id];
      if (!old) changes.push({ supply_name: it.supply_name, added: true, after_qty: it.quantity });
      else if (Math.abs(old.quantity - it.quantity) > 0.001) changes.push({ supply_name: it.supply_name, before_qty: old.quantity, after_qty: it.quantity });
    });
    return changes;
  };

  const changes = diff();
  const subtotal = items.reduce((a, b) => a + b.quantity * b.unit_price, 0);
  const availableToAdd = catalog.filter((s) => !items.some((it) => it.supply_id === s.supply_id));

  const goToConfirm = () => {
    if (items.length === 0) {
      toast.error("Debes dejar al menos una línea. Para vaciar la orden, cancélala.");
      return;
    }
    if (reason.trim().length < 5) {
      toast.error("Indica un motivo (mínimo 5 caracteres)");
      return;
    }
    if (changes.length === 0) {
      toast.error("No hay cambios respecto a la orden actual");
      return;
    }
    setStep("confirm");
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.put(`/purchase-orders/${po.id}/items`, {
        items: items.map((it) => ({ supply_id: it.supply_id, quantity: it.quantity })),
        reason: reason.trim(),
        updated_at: po.updated_at,
      });
      toast.success("OC modificada");
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar los cambios");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
          data-testid="po-edit-modal"
          className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center">
                <Pencil className="w-5 h-5 text-[#1A1A1A]" />
              </div>
              <div>
                <h2 className="font-display font-bold text-lg">Modificar {po.folio}</h2>
                <p className="text-xs text-slate-500">
                  {isApproved ? "Sólo puedes reducir cantidades o quitar líneas" : "Puedes agregar, quitar o cambiar cantidades"}
                </p>
              </div>
            </div>
            <button data-testid="po-edit-close-btn" onClick={onClose} className="w-9 h-9 rounded-xl hover:bg-slate-100 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="overflow-y-auto p-6 space-y-4 flex-1">
            {step === "edit" ? (
              <>
                {isApproved && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-900">
                      Esta OC ya está aprobada: sólo puedes bajar cantidades o quitar líneas. Para comprar
                      algo más, levanta una OC nueva.
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  {items.map((it, idx) => (
                    <div key={idx} data-testid={`po-edit-item-row-${idx}`} className="grid grid-cols-12 gap-2 items-center bg-slate-50 rounded-xl p-3">
                      <div className="col-span-6">
                        {isApproved ? (
                          <div className="text-sm font-semibold">{it.supply_name} <span className="text-xs text-slate-400">({it.unit})</span></div>
                        ) : (
                          <select
                            data-testid={`po-edit-item-supply-${idx}`}
                            value={it.supply_id}
                            onChange={(e) => setItemSupply(idx, e.target.value)}
                            className="w-full px-2.5 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                          >
                            <option value={it.supply_id}>{it.supply_name}</option>
                            {catalog.filter((s) => s.supply_id !== it.supply_id).map((s) => (
                              <option key={s.supply_id} value={s.supply_id}>{s.supply.name}</option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div className="col-span-3">
                        <input
                          data-testid={`po-edit-item-qty-${idx}`}
                          type="number" min="0" step="0.1"
                          value={it.quantity}
                          onChange={(e) => updateQty(idx, e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                        />
                      </div>
                      <div className="col-span-2 text-right text-sm font-mono text-slate-500">{fmtMXN(it.quantity * it.unit_price)}</div>
                      <div className="col-span-1 text-right">
                        <button data-testid={`po-edit-item-remove-${idx}`} onClick={() => removeItem(idx)}
                          className="w-8 h-8 rounded-lg hover:bg-red-50 text-red-500 inline-flex items-center justify-center">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {!isApproved && availableToAdd.length > 0 && (
                  <button data-testid="po-edit-add-item-btn" onClick={addItem}
                    className="px-3 py-2 bg-[#1A1A1A] text-white rounded-xl text-xs font-bold flex items-center gap-1.5 hover:bg-black">
                    <Plus className="w-3.5 h-3.5" /> Agregar línea
                  </button>
                )}

                <div className="flex justify-end text-sm font-mono font-bold text-slate-700">Subtotal: {fmtMXN(subtotal)}</div>

                <div>
                  <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Motivo (obligatorio)</label>
                  <textarea data-testid="po-edit-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                    placeholder="Ej. el proveedor avisó que no tiene el producto"
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                </div>
              </>
            ) : (
              <div className="space-y-3" data-testid="po-edit-confirm-step">
                <div className="text-sm font-bold text-[#1A1A1A]">Confirma los cambios</div>
                <div className="space-y-1.5">
                  {changes.map((c, i) => (
                    <div key={i} data-testid={`po-edit-diff-line-${i}`} className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg px-3 py-2">
                      <span className="font-semibold flex-1 min-w-0 truncate">{c.supply_name}</span>
                      {c.removed ? (
                        <span className="text-red-600 text-xs font-bold">se quitó (antes {c.before_qty})</span>
                      ) : c.added ? (
                        <span className="text-emerald-600 text-xs font-bold">se agregó ({c.after_qty})</span>
                      ) : (
                        <span className="flex items-center gap-1.5 font-mono text-xs font-bold text-amber-700">
                          {c.before_qty} <ArrowRight className="w-3 h-3" /> {c.after_qty}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900">
                  <span className="font-bold">Motivo:</span> {reason}
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Total actual</span>
                  <span className="font-mono">{fmtMXN(po.total)}</span>
                </div>
                <div className="flex justify-between text-base font-bold">
                  <span>Total nuevo</span>
                  <span className="font-mono">{fmtMXN(subtotal)}</span>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
            {step === "edit" ? (
              <>
                <button onClick={onClose} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-100">
                  Cancelar
                </button>
                <button data-testid="po-edit-continue-btn" onClick={goToConfirm}
                  className="btn-primary-chizy px-5 py-2 rounded-xl text-sm font-bold">
                  Continuar
                </button>
              </>
            ) : (
              <>
                <button data-testid="po-edit-back-btn" onClick={() => setStep("edit")}
                  className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-100">
                  Volver
                </button>
                <button data-testid="po-edit-confirm-btn" onClick={submit} disabled={submitting}
                  className="btn-primary-chizy px-5 py-2 rounded-xl text-sm font-bold disabled:opacity-50">
                  {submitting ? "Guardando..." : "Guardar cambios"}
                </button>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
